<template>
  <div class="search-wrapper">
    <div class="search-form">
      <el-row :gutter="16">
        <el-col v-for="(item, index) in searchFilter" :key="index" class="search-form-item" :span="calculateSpan(index, searchFilter)">
          <el-input
            v-if="item.searchItemType === 1"
            v-model="item.value"
            clearable
            :placeholder="`${$t('common.pleaseEnter')}${item.searchItemDisplayName}`"
          />
          <el-input-number
            v-else-if="item.searchItemType === 2"
            v-model="item.value"
            :min="0"
            :placeholder="`${$t('common.pleaseEnter')}${item.searchItemDisplayName}`"
          />
          <el-select
            v-else-if="item.searchItemType === 3"
            v-model="item.value"
            clearable
            collapse-tags
            :placeholder="`${$t('common.pleaseChoose')}${item.searchItemDisplayName}`"
            :multiple="item.searchItemDataMultiple"
          >
            <el-option
              v-for="option in item.searchItemData"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </el-select>
          <el-select
            v-else-if="item.searchItemType === 4"
            v-model="item.value"
            v-loadmore="($event) => selectLoadMore(item, $event)"
            filterable
            remote
            clearable
            collapse-tags
            :remote-method="($event) => selectRemoteMethod($event, item)"
            :placeholder="`${$t('common.pleaseChoose')}${item.searchItemDisplayName}`"
            :multiple="item.searchItemDataMultiple"
            @visible-change="($event) => selectFocus($event, item)"
          >
            <el-option
              v-for="option in item.searchItemData"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </el-select>
          <tree-select
            v-else-if="item.searchItemType === 5"
            v-model="item.value"
            collapse-tags
            :multiple="item.searchItemDataMultiple"
            :placeholder="`${$t('common.pleaseChoose')}${item.searchItemDisplayName}`"
            :tree-data="item.searchItemData"
            :show-node="false"
            :show-search="true"
          />
          <div v-else-if="item.searchItemType === 6" class="rl-date-picker-icon">
            <el-date-picker
              v-model="item.value"
              type="date"
              :placeholder="`${item.searchItemDisplayName}${$t('common.pleaseChoose')}`"
            />
            <svg-icon icon-class="calendar" class="calendar-icon"></svg-icon>
          </div>
          <div v-else-if="item.searchItemType === 7" class="rl-date-picker-icon">
            <el-date-picker
              v-model="item.value"
              type="daterange"
              range-separator="⇀"
              :default-time="['00:00:00', '23:59:59']"
              :start-placeholder="`${item.searchItemDisplayName}${$t('common.start')}`"
              :end-placeholder="`${item.searchItemDisplayName}${$t('common.end')}`"
            ></el-date-picker>
            <svg-icon icon-class="calendar" class="calendar-icon"></svg-icon>
          </div>
          <number-range
            v-else-if="item.searchItemType === 8"
            v-model="item.value"
            :start-placeholder="`${item.searchItemDisplayName}${$t('common.greaterThan')}`"
            :end-placeholder="`${item.searchItemDisplayName}${$t('common.lessThan')}`"
          />
        </el-col>
        <el-col :span="8" class="search-operation">
          <el-button type="primary" class="search-btn" @click="handleSearch">搜索</el-button>
          <el-button class="search-btn" @click="handleReset">重置</el-button>
          <div v-if="isAdvance" class="search-advance-content">
            <div class="search-btn" @click="isAdvance = false">一般搜索</div>
            <span>|</span>
            <div class="search-btn" @click="handleSaveFilter">保存条件</div>
          </div>
          <div v-else class="search-btn" @click="isAdvance = true">高级搜索</div>
        </el-col>
      </el-row>
    </div>
    <div class="search-result">
      <div v-for="(item, index) in searchHistory" :key="index" class="history-item" :class="{ 'history-item-active': item.id === currentHistory.id }">
        <span @click="handleApplyHistory(item, index)">{{ item.name }}</span>
        <i class="el-icon-close history-delete" @click.stop="handleDeleteHistory(item, index)" />
      </div>
    </div>

    <!-- 保存搜索结果弹出框 -->
    <save-search-filter v-if="saveFormDialogVisible" :visible.sync="saveFormDialogVisible" @confirm="saveFilter" />
    <!-- 删除确认框 -->
    <delete-dialog layout="left" :visible.sync="deleteDialogVisible" :content="deleteContent" :loading.sync="deleteLoading" @confirm="handleDeleteConfirm" />
  </div>
</template>

<script>
import { getSettingSearchItems, addSearchHistory, deleteSearchHistory } from "@/api/common";
import request from "@/utils/request";
import NumberRange from "./NumberRange";
import TreeSelect from "./TreeSelect";
import SaveSearchFilter from "./SaveSearchFilter";
import DeleteDialog from "@/components/common/Dialogs/DeleteDialog";

export default {
  name: "SearchForm",
  components: { NumberRange, TreeSelect, SaveSearchFilter, DeleteDialog },
  props: {
    formOptions: {
      type: Object,
      default: () => ({ code: "" })
    },
    options: Array
  },
  data() {
    return {
      // 搜索条件
      searchForm: {
        filter: []
      },
      // 搜索历史
      searchHistory: [],
      // 过滤的filterList
      filterList: [],
      // 搜索类型
      isAdvance: false,
      // 原始数据
      originData: [],
      // 保存条件dialog
      saveFormDialogVisible: false,
      // 删除条件dialog
      deleteDialogVisible: false,
      // 删除描述
      deleteContent: {
        title: this.$t("common.searchDelete.title"),
        description: this.$t("common.searchDelete.description")
      },
      deleteLoading: false,
      // 删除的历史id
      currentHistory: {
        id: undefined,
        index: undefined
      }
    };
  },
  computed: {
    formCode() {
      return this.formOptions.code;
    },
    calculateSpan() {
      return (index, list) => {
        const len = list.length;
        // 默认span
        let span = 8;
        // 个数 <= 3 最后两个变小
        if (len <= 3 && index !== 0) {
          span = 4;
        }

        // 个数 > 3  并且为 3 的倍数时，最后两个变小
        if (len > 3 && (len % 3 === 0)) {
          [len - 1, len - 2].includes(index) && (span = 4);
        }
        return span;
      };
    },
    searchFilter() {
      return this.filterList.filter(item => !item.hidden);
    }
  },
  watch: {
    isAdvance: {
      handler(newValue) {
        // 广播初始化状态
        this.$emit("status");
        if (newValue) {
          this.filterList = this.filterList.map(item => ({ ...item, hidden: false }));
          return;
        }

        this.filterList = this.filterList.map(item => ({ ...item, hidden: item.isInit }));
      }
    }
  },
  created() {
    this.initData();
  },
  methods: {
    // 初始化search
    async initData() {
      // 获取form配置
      const { data, success } = await getSettingSearchItems({ formCode: this.formCode });

      if (!success) {
        return;
      }
      // 获取搜索的参数 和 历史
      const { queryParam = [], queryHistory = [] } = data;

      // 初始化搜索表单
      this.initQueryList(queryParam);
      // 初始化搜索历史
      this.initQueryHistory(queryHistory);
      // 广播初始化状态
      this.$emit("status");
    },
    // 初始化搜索表单
    initQueryList(queryList) {
      queryList.forEach(item => {
        // 初始化value
        const queryItem = Object.assign({ value: undefined }, item);

        // 扩展queryItem 属性
        const queryExtendItem = this.extendQueryItem(queryItem);

        // 添加到搜索条件中
        this.filterList.push(queryExtendItem);
      });

      this.filterList = this.filterList.map(item => ({ ...item, isInit: item.hidden }));

      // 初始化搜索条件
      this.searchForm.filter = this.filterList.filter(item => !item.hidden).map(item => item.searchItemBindingFlag);

      // 执行确定
      this.handleSearch();
    },
    // 扩展搜索条件
    extendQueryItem(queryItem) {
      switch (queryItem.searchItemType) {
        // input
        case 1:
          break;
        // input-number
        case 2:
          break;
        // select
        case 3:
          this.initSelectOptions(queryItem);
          break;
        // select-page
        case 4:
          // select触底加载更多
          queryItem = Object.assign(queryItem, { searchItemData: [], pageNum: 1, pageSize: 10, total: 1 });
          break;
        // cascadar
        case 5:
          this.initCascadarOptions(queryItem);
          break;
        // date
        case 6:
          break;
        // date-range
        case 7:
          break;
        // num-range
        case 8:
          break;
      }

      return queryItem;
    },
    // 初始化下拉选择的options
    initSelectOptions(item) {
      // 解构重命名
      const { searchItemData = [], searchItemDataLabel: label, searchItemDataValue: key, searchItemDataChildren: children } = item;

      // 存在初始化下拉选项
      if (Array.isArray(searchItemData) && searchItemData.length) {
        item["searchItemData"] = this.formatOptions(searchItemData, label, key, children).filter(item => !item.hidden);
        return;
      }

      // 初始化 searchItemData
      item["searchItemData"] = [];

      // 异步初始化下拉选项
      this.autoRequest(item).then(res => {
        const { success, data } = res;
        if (!success) {
          return;
        }

        // data本身支持
        if (Array.isArray(data)) {
          item["searchItemData"] = this.formatOptions(data, label, key, children).filter(item => !item.hidden);
          return;
        }

        // 兼容其他数据
        const { dictItems = [], records = [] } = data;
        // 优先使用dict 后使用records
        item["searchItemData"] = this.formatOptions(dictItems.length ? dictItems : records, label, key, children).filter(item => !item.hidden);
      });
    },
    // 初始化选项
    initCascadarOptions(item) {
      item["searchItemData"] = [];
      // 解构重命名
      this.autoRequest(item);
    },
    // 自动发送请求
    autoRequest(item, params = {}) {
      const { searchItemType: paging, pageNum, pageSize, searchItemDataRequestMethod: requestMethod, searchItemDataParams: requestParams, searchItemDataService: requestService, searchItemDataUrl: requestUrl } = item;

      // 处理异常情况
      if (!requestMethod || !requestUrl) {
        return;
      }

      // 拼装请求数据
      const config = { method: undefined, url: undefined, params: undefined, data: undefined };

      /*
       * 解析url
       * let url = requestService === '/admin' ? `${requestService}${requestUrl}` : `/scim-edm-service${requestService}${requestUrl}`;
       */
      let url = `${requestService}${requestUrl}`;
      // 正则匹配tenantId，替换参数
      url = url.replace(/\{tenantId\}/g, this.$store.getters.tenantId);
      // 解析方法
      const method = requestMethod.toLowerCase();
      // 解析params
      requestParams && requestParams.replace(/\{.*\}/g, match => {
        const query = match.substring(1, match.length - 1);
        query.split(",").forEach(item => {
          const [key, value] = item.split("=");
          params[key] = value;
        });
      });

      config.url = url;
      config.method = method;

      // 使用post & 使用分页
      if (method === "post" && paging) {
        config.url = `${config.url}?page=${pageNum}&size=${pageSize}`;
        config.data = params;
      }

      return request(config);
    },
    // 转换options
    formatOptions(list, label, key, children = "children") {
      list.forEach(item => {
        // 处理label和value
        item.label = label.split(",").map(key => item[key]).join("-");
        item.value = item[key];
        item["children"] = item[children];

        if (Array.isArray(item["children"])) {
          this.formatOptions(item["children"], label, key, children);
        }
      });

      return list;
    },
    // 初始化搜索历史
    initQueryHistory(historyList) {
      this.searchHistory = historyList;
    },
    // 确定
    handleSearch() {
      const queryParam = this.getSearchParams();
      this.$emit("search", queryParam);
    },
    // 保存搜索条件
    async saveFilter(name) {
      const queryParam = this.getSearchParams();
      try {
        const { success, data } = await addSearchHistory({ formCode: this.formCode, data: { name, queryParam }});
        if (data.queryHistory && success) {
          this.searchHistory = data.queryHistory;
          this.$message.success("保存成功");
        }
      } catch (error) {
        throw new Error(error);
      }
    },
    // 删除历史保存
    handleDeleteHistory(history, index) {
      this.currentHistory = { id: history.id, index };
      this.deleteDialogVisible = true;
    },
    // 删除提交
    async handleDeleteConfirm() {
      if (!this.currentHistory) {
        return;
      }
      this.deleteLoading = true;
      try {
        const { success } = await deleteSearchHistory({ formCode: this.formCode, id: this.currentHistory.id });
        success && this.searchHistory.splice(this.currentHistory.index, 1);
        this.deleteLoading = false;
        this.deleteDialogVisible = false;
        this.$message.success(this.$t("common.deleteSuccess"));
      } catch (error) {
        this.deleteLoading = false;
        throw new Error(error);
      }
    },
    // 重置
    handleReset() {
      this.filterList.forEach(item => {
        item.value = undefined;
      });

      this.handleSearch();
    },
    // 应用历史条件
    handleApplyHistory(item, index) {
      this.currentHistory = { id: item.id, index };
      const { queryParam } = item;
      let isAdvance = false;
      for (const key in queryParam) {
        this.filterList.forEach(item => {
          if (item.searchItemBindingFlag === key) {
            item.value = queryParam[key];

            if (item.isInit) {
              isAdvance = true;
            }
          }
        });
      }

      this.isAdvance = isAdvance;
    },
    // 保存当前结果
    handleSaveFilter() {
      if (this.searchHistory.length >= 5) {
        this.$message.warning("搜索结果最多添加5个");
        return;
      }
      this.saveFormDialogVisible = true;
    },
    // 获取搜索参数
    getSearchParams() {
      return this.filterList.reduce((acc, cur) => {
        let { value } = cur;
        const { searchItemType, searchItemBindingFlag, hidden } = cur;
        // 去除空格
        (typeof value === "string") && (value = value.trim());

        if (value === undefined || value === null || value === "" || value === "undefined") {
          return acc;
        }

        if (hidden) {
          return acc;
        }

        // 数组值
        if (Array.isArray(value)) {
          if (searchItemType === 7) {
            value = value.map((item, index) => (index === 0 ? `${this.dayjs(item).format("YYYY-MM-DD")} 00:00:00` : `${this.dayjs(item).format("YYYY-MM-DD")} 23:59:59`));
          } else if (searchItemType === 8) {
            // 初始化第二个值
            value.length === 1 && (value[1] = undefined);

            value = value.map((item, index) => {
              if (index === 0) {
                return item || 0;
              }

              return item === undefined || item === null || item === "" ? "MAX_VALUE" : item;
            });
          }
        } else {
          // 值为其他
          if (searchItemType === 6) {
            value = `${this.dayjs(value).format("YYYY-MM-DD")} 00:00:00`;
          }
        }

        acc[searchItemBindingFlag] = value;
        return acc;
      }, {});
    }
  }
};
</script>

<style lang='scss' scoped>

.search-wrapper {
  padding: 24px 0;
  .search-form {
    transition: all 0.5s;
    .search-form-item {
      margin-bottom: 16px;
    }
  }

  .search-operation {
    height: 32px;
    line-height: 32px;
    display: flex;
    align-items: center;

    .search-advance-content {
      display: inline-block;
    }

    .search-btn {
      width: 80px;
      height: 100%;
      display: inline-block;
      text-align: center;
      cursor: pointer;
    }
  }

  .search-result {
    margin-top: 16px;
    display: flex;
    align-items: center;

    .history-item {
      display: inline-flex;
      align-items: center;
      justify-content: space-around;
      width: 76px;
      height: 20px;
      line-height: 20px;
      border-radius: 10px;
      box-sizing: border-box;
      cursor: pointer;
      font-size: 12px;
      background: #EAEDF3;
      border: 1px solid #D4D9E2;
      color: #505363;
      margin-right: 10px;

      i {
        width: 20px;
      }

      span {
        flex: 1;
        padding-left: 8px;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      }

      &:hover {
        background-color: #E7F7EF;
        border: 1px solid #AFE6CA;
        color: #00B259;
      }
    }

    .history-item-active {
      background-color: #E7F7EF;
      border: 1px solid #AFE6CA;
      color: #00B259;
    }

    .history-delete {
    }
  }

  .rl-date-picker-icon {
    position: relative;

    .calendar-icon {
      position: absolute;
      top: 50%;
      right: 8px;
      transform: translate(0, -50%);
    }
  }

  ::v-deep .el-select {
    width: 100%;
  }

  ::v-deep .el-date-editor .el-range-separator {
    line-height: 24px;
  }
}
</style>

